import React, { useState, useEffect, useRef } from 'react';
import { Button, TextField, IconButton } from '@mui/material';
import { CircleUserRound, Moon, Search, Sun, X } from 'lucide-react';
import { Bell } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import '../../css/HRPortal.css';
import axios from 'axios';
import host from '../../global';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { signOut, getCurrentUser } from '../../utils/authSession';
import { identifyUser, setCompany } from '../../utils/analytics';
import { LogOut, UserPen } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setIsAdminLoggedIn } from '../../store/slices/interviewCommentsSlice';
import { useTheme } from '../../hooks/useTheme';
import {
  clearCandidateManagementSlice,
  updateNavigationTab,
} from '../../store/slices/candidateManagementSlice';
import { clearInterviewManagementSlice } from '../../store/slices/InterviewManagementSlice';
import {
  clearFilterSlice,
  removeActiveFilter,
  updateCurrentJobRole,
} from '../../store/slices/filterOptionsSlice';
import { updateUserData } from '../../store/slices/userSlice';
import { useSourcingConfig } from '../../contexts/SourcingConfigContext';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sourcingEnabled } = useSourcingConfig();
  const [notification, setNotification] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const accessToken = Cookies.get('accessToken');
  const [userData, setUserData] = useState(null);
  const [userId, setUserId] = useState(null);
  const hasUpdatedLastLogin = useRef(false);

  const { theme, toggleTheme } = useTheme();

  // State To Check Current User Is Admin Or Not For Interview Comments Part | Dont Remove this else it can break Interview Comments Feature
  const dispatch = useDispatch();
  const navigationTab = useSelector(
    (state) => state.candidateManagementSlice.navigationTab,
  );
  const isAdminLoggedIn = useSelector(
    (state) => state.interviewCommentsSlice.isAdminLoggedIn,
  );

  const availablePages = [
    { name: 'Home', route: `/hr/dashboard` },
    { name: 'Candidate Reports', route: `/hr/candidatereports` },
    { name: 'Job Vacancies', route: `/hr/jobvacancies` },
    { name: 'Profile', route: `/hr/hreditprofile` },
    { name: 'Analyze Resume', route: `/hr/checkresume` },
  ];

  const updateHrLastLogin = async () => {
    try {
      await axios.post(
        `${host}/update_hr_last_login`,
        {
          timestamp: new Date().toISOString(), // Send current system time in body
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`, // Send token in headers
          },
        },
      );
    } catch (error) {
      console.error('Error updating HR last login', error);
      // Silent fail — this is a background task
    }
  };

  const fetchProfileData = async () => {
    try {
      const { userId } = await getCurrentUser();
      setUserId(userId);
      const response = await axios.get(`${host}/gethrdetails`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      dispatch(setIsAdminLoggedIn(false));
      dispatch(updateUserData(response.data));
      setUserData(response.data);
      // Runs on every login and every app load with a session
      identifyUser(userId, {
        email: response.data.email,
        name: response.data.name,
        phone: response.data.contact,
      });
      setCompany(response.data.org_id);

      if (response.data.name) {
        localStorage.setItem('username', response.data.name);
      }
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data.detail;
        if (status === 401) {
          await new Promise((resolve) => {
            Cookies.remove('accessToken');
            resolve();
          });
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 404) {
          toast.warning(`Not Found: ${detail}`);
        } else {
          toast.error(`Error: ${detail}`);
        }
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }
    }
  };

  useEffect(() => {
    fetchProfileData();
    // Only update last login if we have a valid access token and haven't updated yet
    if (accessToken && !hasUpdatedLastLogin.current) {
      console.log('Calling updateHrLastLogin for the first time');
      updateHrLastLogin();
      hasUpdatedLastLogin.current = true;
    } else if (accessToken && hasUpdatedLastLogin.current) {
      console.log('updateHrLastLogin already called, skipping');
    }
  }, [accessToken]);

  const handleSignOut = async () => {
    try {
      await signOut();
      dispatch(clearCandidateManagementSlice());
      dispatch(clearInterviewManagementSlice({ clearJobRoles: true }));
      dispatch(clearFilterSlice());
      navigate('/');
    } catch (error) {
      console.error('Error signing out', error);
      toast.error('Error signing out');
    }
  };

  const profileImageUrl = userData?.profile
    ? `${process.env.REACT_APP_PROFILE_PHOTOS_HOST}/HRProfiles/${userId}_${userData.profile}`
    : '';

  const handleSearchInputChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    if (query) {
      const filteredSuggestions = availablePages.filter((page) =>
        page.name.toLowerCase().includes(query.toLowerCase()),
      );
      setSuggestions(filteredSuggestions);
      setNoResults(filteredSuggestions.length === 0);
    } else {
      setSuggestions([]);
      setNoResults(false);
    }
  };

  const handleSuggestionClick = (route) => {
    navigate(route);
    setSearchQuery('');
    setSuggestions([]);
  };

  const handlenotificationClick = (upload_id) => {
    navigate(`/hr/BulkResume/Bulkresumemodel/${upload_id}`);
  };

  useEffect(() => {
    const fetchBulkResumeUploadResult = async () => {
      try {
        const response = await axios.get(`${host}/resumes/bulk`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            jd_id: 'all',
          },
        });
        setNotification(response.data.notifications);
      } catch (error) {
        if (error.response) {
          const status = error.response.status;
          const detail = error.response.data.detail;
          if (status === 401) {
            await new Promise((resolve) => {
              Cookies.remove('accessToken');
              resolve();
            });
            toast.error(`Unauthorized: ${detail}`);
          } else if (status === 404) {
            toast.warning(`Not Found: ${detail}`);
          } else {
            toast.error(`Error: ${detail}`);
          }
        } else {
          toast.error('An unexpected error occurred. Please try again.');
        }
      }
    };
    fetchBulkResumeUploadResult();
  }, [accessToken]);

  return (
    <>
      <header className="main-header flex justify-between items-center py-3 px-8 gap-4 flex-nowrap box-border border-b border-white/10 grad">
        <nav className="flex flex-wrap items-center min-w-0 flex-1">
          <>
            {sourcingEnabled && (
              <button
                onClick={() => {
                  dispatch(updateNavigationTab('ALL'));
                  navigate('/hr/find-candidates');
                }}
                data-text="Find Candidates"
                className={`relative block text-base
                ${
                  location.pathname.includes('find-candidates')
                    ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                    : 'text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
                }

                before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
              `}
              >
                Find Candidates
                {location.pathname.includes('find-candidates') && (
                  <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4/5 h-[3px] bg-gradient-to-r from-pink-500 to-blue-500"></span>
                )}
              </button>
            )}
            <button
              onClick={() => {
                navigate('/hr/checkresume');
                dispatch(updateNavigationTab('ALL'));
              }}
              className={`relative block text-base
              ${
                location.pathname.includes('checkresume')
                  ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                  : 'text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
              }

              before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
            `}
            >
              Screen a Resume
              {location.pathname.includes('checkresume') && (
                <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4/5 h-0.5 bg-gradient-to-r from-pink-500 to-blue-500"></span>
              )}
            </button>
            {/* <button
                onClick={() => navigate("/hr/job-applicants")}
                className={`${
                  location.pathname.includes("job-applicants")
                    ? "text-transparent bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text relative"
                    : "dark:text-gray-300 text-textPrimary"
                } relative text-sm font-normal`}
              >
                Screened Candidates
                {location.pathname.includes("job-applicants") && (
                  <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4/5 h-0.5 bg-gradient-to-r from-pink-500 to-blue-500"></span>
                )}
              </button> */}
            <button
              onClick={() => {
                dispatch(updateCurrentJobRole('All Job Roles'));
                dispatch(removeActiveFilter('JOB_ROLE'));
                dispatch(updateNavigationTab('SOURCED'));
                navigate('/hr/schedule-interview');
              }}
              data-text="Screened Candidates"
              className={`relative block text-base
                              ${
                                navigationTab === 'SOURCED'
                                  ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                                  : 'text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
                              }
              
                              before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
                            `}
            >
              Screened Candidates
              {navigationTab === 'SOURCED' && (
                <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4/5 h-[3px] bg-gradient-to-r from-pink-500 to-blue-500"></span>
              )}
            </button>
            {/* <button
                onClick={() => navigate("/hr/candidatereports")}
                className={`${
                  location.pathname.includes("candidatereports")
                    ? "text-transparent bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text relative"
                    : "dark:text-gray-300 text-textPrimary"
                } relative text-sm font-normal`}
              >
                Scheduled Interviews
                {location.pathname.includes("candidatereports") && (
                  <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4/5 h-0.5 bg-gradient-to-r from-pink-500 to-blue-500"></span>
                )}
              </button> */}
            <button
              onClick={() => {
                dispatch(updateCurrentJobRole('All Job Roles'));
                dispatch(removeActiveFilter('JOB_ROLE'));
                dispatch(updateNavigationTab('INTERVIEW'));
                navigate('/hr/candidate-management');
              }}
              data-text="Interview Management"
              className={`relative block text-base
                               ${
                                 navigationTab === 'INTERVIEW'
                                   ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                                   : 'text-textPrimary dark:text-gray-300 hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
                               }
               
                               before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
                             `}
            >
              Interview Management
              {navigationTab === 'INTERVIEW' && (
                <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4/5 h-[3px] bg-gradient-to-r from-pink-500 to-blue-500"></span>
              )}
            </button>
          </>
        </nav>
        <div className="main-header-actions flex items-center gap-4 shrink-0">
          {/* {location.pathname == "/hr/dashboard" && (
            <>
              <div className="relative">
                <div className="flex items-center py-1.5 px-2 rounded-full w-64 border border-gray-600">
                  <span className="text-gray-400 mr-2 ml-2 flex items-center">
                    <Search size={16} />
                  </span>
                  <input
                    type="text"
                    placeholder="Search here..."
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    className="bg-transparent text-gray-300 placeholder-gray-500 flex-1 outline-none border-none focus:outline-none focus:ring-0 text-sm"
                  />
                  {searchQuery && (
                    <button
                      onClick={() =>
                        handleSearchInputChange({ target: { value: "" } })
                      }
                      className="text-gray-400 hover:text-gray-200 mr-2 flex items-center"
                      aria-label="Clear search"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                {suggestions.length > 0 && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-gray-800 rounded-lg shadow-lg z-10">
                    {suggestions.map((page) => (
                      <div
                        key={page.route}
                        onClick={() => handleSuggestionClick(page.route)}
                        className="p-2 text-white hover:bg-gray-700 cursor-pointer"
                      >
                        {page.name}
                      </div>
                    ))}
                  </div>
                )}
                {noResults && searchQuery && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-gray-800 rounded-lg shadow-lg z-10 p-2 text-white">
                    No results found
                  </div>
                )}
              </div>
            </>
          )} */}
          <div className="group relative ml-2 z-[99999]">
            <div className="group cursor-pointer">
              <img
                className="w-[27px] h-[27px] rounded-full border-1 border-white group-hover:hidden transition-all"
                src="/profileheader.png"
                alt="Profile"
              />
              <img
                className="w-[27px] h-[27px] rounded-full border-1 border-white hidden group-hover:block transition-all"
                src="/profileheaderhover.png"
                alt="Profile Hover"
              />
            </div>
            <div className="hidden group-hover:flex group-hover:items-start absolute top-full right-0 z-50 p-1 flex-col">
              <div
                className="w-[250px] rounded-2xl shadow-2xl overflow-hidden"
                style={{ border: 'solid 0.2px purple' }}
              >
                <div className="relative">
                  {userData ? (
                    <div className="dark:bg-gradient-to-br bg-bgPrimary from-[rgba(34,52,97,0.25)] to-[rgba(11,10,31,0.05)] dark:backdrop-blur-[50px]">
                      <div className="relative z-10 flex flex-col sm:flex-col items-center p-2">
                        {profileImageUrl ? (
                          <img
                            className="w-[110px] h-[110px] rounded-full border-4 border-white shadow-lg object-cover"
                            src={profileImageUrl}
                            alt={
                              userData?.name
                                ? userData.name[0].toUpperCase()
                                : 'Profile Image'
                            }
                          />
                        ) : (
                          <img
                            className="w-[90px] h-[90px] rounded-full border-1 border-white shadow-lg object-cover p-4 mb-2"
                            src="/profileheaderhover.png"
                          />
                        )}
                        <div className="flex flex-col gap-2 justify-center items-center">
                          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-blue-500">
                            {userData?.name || 'No Name Available'}
                          </h2>
                          <p className="text-medium text-white/90 font-medium -mt-2">
                            {userData?.department || 'No Department'}
                          </p>
                          <div className="flex justify-center sm:justify-start gap-6 p-2 mb-2">
                            <UserPen
                              className="text-blue-600 bg-blue-100 rounded-full p-2 w-10 h-10 hover:bg-blue-200 hover:text-blue-700 transition-all duration-300 cursor-pointer"
                              onClick={() => navigate(`/hr/hreditprofile`)}
                            />
                            <LogOut
                              className="text-red-600 bg-red-100 rounded-full p-2 w-10 h-10 hover:bg-red-200 hover:text-red-700 transition-all duration-300 cursor-pointer"
                              onClick={handleSignOut}
                            />
                            <button
                              onClick={toggleTheme}
                              className="rounded-full p-2 w-10 h-10 dark:bg-gray-800 bg-yellow-200 text-textPrimary transition-colors flex items-center justify-center"
                            >
                              {theme === 'dark' ? (
                                <Moon className="w-5 h-5 text-gray-200" />
                              ) : (
                                <Sun className="w-5 h-5 text-yellow-600" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div class="relative flex w-64 animate-pulse gap-2 p-4">
                      <div class="h-12 w-12 rounded-full dark:bg-slate-400 bg-gray-200"></div>
                      <div class="flex-1">
                        <div class="mb-1 h-5 w-3/5 rounded-lg dark:bg-slate-400 bg-gray-200 text-lg"></div>
                        <div class="h-5 w-[90%] rounded-lg dark:bg-slate-400 bg-gray-200 text-sm"></div>
                      </div>
                      <div class="absolute bottom-5 right-0 h-4 w-4 rounded-full dark:bg-slate-400 bg-gray-200"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
export default Header;
